import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Search, 
  Building2, 
  Clock, 
  ChevronRight,
  MapPin,
  Star,
  StarOff,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';

const LEVEL_COLORS = {
  national: 'bg-purple-500/20 text-purple-600 border-purple-500/30',
  regional: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
  state: 'bg-green-500/20 text-green-600 border-green-500/30',
  local: 'bg-orange-500/20 text-orange-600 border-orange-500/30',
  franchise: 'bg-cyan-500/20 text-cyan-600 border-cyan-500/30',
  franchisee: 'bg-teal-500/20 text-teal-600 border-teal-500/30',
};

const STORAGE_KEY = 'riskhero-recent-orgs';
const FAVORITES_KEY = 'riskhero-favorite-orgs';
const MAX_RECENT = 5;

// Fuzzy search function
const fuzzyMatch = (str, pattern) => {
  if (!pattern) return true;
  const string = str.toLowerCase();
  const search = pattern.toLowerCase();
  
  // Direct includes match (highest priority)
  if (string.includes(search)) return { match: true, score: 100 };
  
  // Fuzzy match
  let searchIdx = 0;
  let score = 0;
  
  for (let i = 0; i < string.length && searchIdx < search.length; i++) {
    if (string[i] === search[searchIdx]) {
      score += 10;
      // Bonus for consecutive matches
      if (i > 0 && string[i - 1] === search[searchIdx - 1]) {
        score += 5;
      }
      searchIdx++;
    }
  }
  
  return { 
    match: searchIdx === search.length, 
    score: searchIdx === search.length ? score : 0 
  };
};

// Build hierarchy path for an org
const getHierarchyPath = (org, allOrgs) => {
  const path = [];
  let current = org;
  
  while (current?.parent_id) {
    const parent = allOrgs.find(o => o.id === current.parent_id);
    if (parent) {
      path.unshift(parent.name);
      current = parent;
    } else {
      break;
    }
  }
  
  return path;
};

export default function OrgSearchPicker({ 
  organizations = [], 
  selectedOrgId, 
  onSelect, 
  trigger,
  placeholder = "Search organizations..."
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [recentOrgs, setRecentOrgs] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Load recent and favorites from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setRecentOrgs(JSON.parse(stored));
      
      const favs = localStorage.getItem(FAVORITES_KEY);
      if (favs) setFavoriteIds(JSON.parse(favs));
    } catch (e) {
      console.error('Failed to load recent orgs');
    }
  }, []);

  // Save recent org on selection
  const saveRecentOrg = useCallback((org) => {
    const updated = [org.id, ...recentOrgs.filter(id => id !== org.id)].slice(0, MAX_RECENT);
    setRecentOrgs(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [recentOrgs]);

  // Toggle favorite
  const toggleFavorite = useCallback((orgId, e) => {
    e.stopPropagation();
    const updated = favoriteIds.includes(orgId)
      ? favoriteIds.filter(id => id !== orgId)
      : [...favoriteIds, orgId];
    setFavoriteIds(updated);
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [favoriteIds]);

  // Filter and sort organizations
  const getFilteredOrgs = useCallback(() => {
    let results = organizations.map(org => {
      const nameMatch = fuzzyMatch(org.name, search);
      const cityMatch = fuzzyMatch(org.city || '', search);
      const stateMatch = fuzzyMatch(org.state || '', search);
      
      const maxScore = Math.max(nameMatch.score, cityMatch.score * 0.8, stateMatch.score * 0.6);
      const matches = nameMatch.match || cityMatch.match || stateMatch.match;
      
      return { ...org, searchScore: maxScore, matches };
    });

    // Filter by search
    if (search) {
      results = results.filter(org => org.matches);
    }

    // Sort by: favorites first, then search score, then level, then name
    const levelOrder = { national: 0, regional: 1, state: 2, local: 3, franchise: 4, franchisee: 5 };
    
    results.sort((a, b) => {
      // Favorites first
      const aFav = favoriteIds.includes(a.id) ? 0 : 1;
      const bFav = favoriteIds.includes(b.id) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      
      // Search score (higher is better)
      if (search && a.searchScore !== b.searchScore) {
        return b.searchScore - a.searchScore;
      }
      
      // Level order
      const levelA = levelOrder[a.level] ?? 99;
      const levelB = levelOrder[b.level] ?? 99;
      if (levelA !== levelB) return levelA - levelB;
      
      // Alphabetical
      return a.name.localeCompare(b.name);
    });

    return results;
  }, [organizations, search, favoriteIds]);

  // Get recent orgs that still exist
  const getRecentOrgObjects = useCallback(() => {
    return recentOrgs
      .map(id => organizations.find(o => o.id === id))
      .filter(Boolean);
  }, [recentOrgs, organizations]);

  const filteredOrgs = getFilteredOrgs();
  const recentOrgObjects = getRecentOrgObjects();
  const showRecent = !search && recentOrgObjects.length > 0;

  // Combined list for keyboard navigation
  const allItems = showRecent 
    ? [...recentOrgObjects.map(o => ({ ...o, isRecent: true })), ...filteredOrgs.filter(o => !recentOrgs.includes(o.id))]
    : filteredOrgs;

  const selectedOrg = organizations.find(o => o.id === selectedOrgId);

  // Handle selection
  const handleSelect = useCallback((org) => {
    onSelect(org.id);
    saveRecentOrg(org);
    setOpen(false);
  }, [onSelect, saveRecentOrg]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => Math.min(prev + 1, allItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (allItems[highlightedIndex]) {
            handleSelect(allItems[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, highlightedIndex, allItems, handleSelect]);

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setSearch('');
      setHighlightedIndex(0);
    }
  }, [open]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const item = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Render organization item
  const renderOrgItem = (org, index, isRecent = false) => {
    const hierarchyPath = getHierarchyPath(org, organizations);
    const isHighlighted = index === highlightedIndex;
    const isFavorite = favoriteIds.includes(org.id);
    const isSelected = org.id === selectedOrgId;

    return (
      <div
        key={`${isRecent ? 'recent-' : ''}${org.id}`}
        data-index={index}
        onClick={() => handleSelect(org)}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg mx-1 transition-colors",
          isHighlighted && "bg-primary/10",
          isSelected && !isHighlighted && "bg-secondary/50",
          !isHighlighted && !isSelected && "hover:bg-secondary/50"
        )}
      >
        <Building2 className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("font-medium truncate", isSelected && "text-primary")}>
              {org.name}
            </span>
            <Badge 
              variant="outline" 
              className={cn("text-[10px] uppercase flex-shrink-0", LEVEL_COLORS[org.level])}
            >
              {org.level}
            </Badge>
          </div>
          
          {(hierarchyPath.length > 0 || org.city) && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              {hierarchyPath.length > 0 && (
                <>
                  <span className="truncate">{hierarchyPath.join(' › ')}</span>
                  {org.city && <span>•</span>}
                </>
              )}
              {org.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {org.city}, {org.state}
                </span>
              )}
            </div>
          )}
        </div>

        <button
          onClick={(e) => toggleFavorite(org.id, e)}
          className="p-1 hover:bg-secondary rounded opacity-50 hover:opacity-100 transition-opacity"
        >
          {isFavorite ? (
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
          ) : (
            <StarOff className="w-4 h-4" />
          )}
        </button>

        {isSelected && (
          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
        )}
      </div>
    );
  };

  return (
    <>
      {/* Trigger Button */}
      <div onClick={() => setOpen(true)} className="cursor-pointer">
        {trigger || (
          <div className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-secondary/50 transition-colors min-w-[280px]">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 truncate">
              {selectedOrg?.name || placeholder}
            </span>
            {selectedOrg && (
              <Badge variant="outline" className={cn("text-[10px] uppercase", LEVEL_COLORS[selectedOrg.level])}>
                {selectedOrg.level}
              </Badge>
            )}
            <Search className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Search Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
          {/* Search Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b">
            <Search className="w-5 h-5 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, city, or state..."
              className="border-0 p-0 h-auto text-base focus-visible:ring-0 placeholder:text-muted-foreground/60"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="p-1 hover:bg-secondary rounded"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Results */}
          <ScrollArea className="max-h-[400px]" ref={listRef}>
            <div className="py-2">
              {/* Recent Section */}
              {showRecent && (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    Recent
                  </div>
                  {recentOrgObjects.map((org, idx) => renderOrgItem(org, idx, true))}
                  
                  <div className="my-2 mx-4 border-t" />
                  
                  <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    All Organizations ({filteredOrgs.length})
                  </div>
                </>
              )}

              {/* Search Results / All Orgs */}
              {search && (
                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {filteredOrgs.length} result{filteredOrgs.length !== 1 ? 's' : ''} for &ldquo;{search}&rdquo;
                </div>
              )}

              {(showRecent ? filteredOrgs.filter(o => !recentOrgs.includes(o.id)) : filteredOrgs).map((org, idx) => {
                const actualIndex = showRecent ? recentOrgObjects.length + idx : idx;
                return renderOrgItem(org, actualIndex);
              })}

              {filteredOrgs.length === 0 && (
                <div className="px-4 py-8 text-center text-muted-foreground">
                  <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No organizations found</p>
                  <p className="text-sm">Try a different search term</p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-4 py-2 border-t bg-secondary/30 text-xs text-muted-foreground flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px]">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px]">Enter</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px]">Esc</kbd>
                Close
              </span>
            </div>
            <span>{organizations.length} organizations</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
